import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useState, type FormEvent } from 'react'
import { LockKey, ShieldCheck } from '@phosphor-icons/react'
import { api, ApiError } from './lib/api'
import { authClient } from './lib/auth-client'
import { Button, Field, LoadingScreen } from './components/common'
import { AppShell } from './components/AppShell'
import { TakeoverPage } from './pages/TakeoverPage'
import { ProjectsPage } from './pages/ProjectsPage'
import { ReadinessPage } from './pages/ReadinessPage'
import { KnowledgePage } from './pages/KnowledgePage'
import { WorkingItemsPage } from './pages/WorkingItemsPage'
import { SowPage } from './pages/SowPage'
import { RisksPage } from './pages/RisksPage'
import { PlaceholderPage } from './pages/PlaceholderPage'
import { UsersPage } from './pages/UsersPage'
import { WeeklyReportPage } from './pages/WeeklyReportPage'

function LoginOrSetup() {
  const queryClient = useQueryClient()
  const status = useQuery({ queryKey: ['setup-status'], queryFn: api.setupStatus })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [bootstrapKey, setBootstrapKey] = useState('')
  const [error, setError] = useState('')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    try {
      if (status.data?.needsSetup) {
        await api.setupAdmin({ email, name, bootstrapKey })
        await status.refetch()
        setPassword('12345')
        setError('管理員已建立，請使用預設密碼 12345 登入。')
        return
      }
      const result = await authClient.signIn.email({ email, password })
      if (result.error) throw new Error(result.error.message ?? '登入失敗。')
      await queryClient.invalidateQueries({ queryKey: ['me'] })
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '操作失敗。')
    }
  }

  if (status.isLoading) return <LoadingScreen />
  const isSetup = Boolean(status.data?.needsSetup)
  return (
    <main className="auth-page">
      <section className="auth-brand-panel">
        <div className="brand-emblem">ESS</div>
        <div>
          <span className="eyebrow light">WEEKLY READINESS</span>
          <h1>把交接進度變成<br />團隊的共同畫面</h1>
          <p>同一份資料、清楚的責任、每一次修改都有紀錄。</p>
        </div>
        <div className="auth-security-note"><ShieldCheck size={20} /> 僅受邀帳號可以進入</div>
      </section>
      <section className="auth-form-panel">
        <form className="auth-card" onSubmit={submit}>
          <div className="auth-icon"><LockKey size={24} /></div>
          <h2>{isSetup ? '建立第一位管理員' : '登入 ESS Dashboard'}</h2>
          <p>{isSetup ? '這個步驟只會在系統尚未建立帳號時出現。' : '請使用管理員建立的公司信箱帳號。'}</p>
          {isSetup && (
            <>
              <Field label="顯示名稱"><input value={name} onChange={(e) => setName(e.target.value)} required autoFocus /></Field>
              <Field label="初始化金鑰"><input type="password" autoComplete="off" value={bootstrapKey} onChange={(e) => setBootstrapKey(e.target.value)} required /></Field>
            </>
          )}
          <Field label="Email"><input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus={!isSetup} /></Field>
          {!isSetup ? <Field label="密碼"><input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></Field> : null}
          {error && <div className={error.includes('已建立') ? 'form-message success' : 'form-message error'}>{error}</div>}
          <Button variant="primary" type="submit">{isSetup ? '建立管理員' : '登入'}</Button>
          {isSetup && <small>第一組密碼固定為 12345，首次登入後必須立即更換。</small>}
        </form>
      </section>
    </main>
  )
}

function ChangePasswordPage() {
  const queryClient = useQueryClient()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const mutation = useMutation({
    mutationFn: () => api.changePassword(currentPassword, newPassword),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
    onError: (caught) => setError(caught instanceof Error ? caught.message : '密碼更新失敗。'),
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setError('')
    if (newPassword !== confirm) return setError('兩次輸入的新密碼不一致。')
    mutation.mutate()
  }

  return (
    <main className="auth-page compact">
      <section className="auth-brand-panel">
        <div className="brand-emblem">ESS</div>
        <div><span className="eyebrow light">ACCOUNT SECURITY</span><h1>第一次登入，<br />先建立自己的密碼</h1></div>
      </section>
      <section className="auth-form-panel">
        <form className="auth-card" onSubmit={submit}>
          <h2>更改預設密碼</h2>
          <p>新密碼至少需要 10 個字元；完成後其他裝置的舊 Session 會失效。</p>
          <Field label="目前密碼"><input type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required autoFocus /></Field>
          <Field label="新密碼"><input type="password" autoComplete="new-password" minLength={10} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required /></Field>
          <Field label="再次輸入新密碼"><input type="password" autoComplete="new-password" minLength={10} value={confirm} onChange={(e) => setConfirm(e.target.value)} required /></Field>
          {error && <div className="form-message error">{error}</div>}
          <Button variant="primary" type="submit" disabled={mutation.isPending}>{mutation.isPending ? '更新中…' : '更新密碼並繼續'}</Button>
        </form>
      </section>
    </main>
  )
}

export default function App() {
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  if (me.isLoading) return <LoadingScreen />
  if (me.error instanceof ApiError && me.error.status === 401) return <LoginOrSetup />
  if (me.error) return <div className="center-screen error-screen"><strong>無法載入帳號資訊</strong><p>{me.error.message}</p></div>
  if (!me.data) return <LoginOrSetup />
  if (me.data.user.mustChangePassword) return <ChangePasswordPage />

  return (
    <Routes>
      <Route element={<AppShell session={me.data} />}>
        <Route index element={<Navigate to="/takeover" replace />} />
        <Route path="/takeover" element={<TakeoverPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/readiness" element={<ReadinessPage />} />
        <Route path="/sow" element={<SowPage />} />
        <Route path="/contacts" element={<PlaceholderPage title="Contacts" description="聯絡窗口將在下一階段納入資料庫管理。" />} />
        <Route path="/knowledge" element={<KnowledgePage />} />
        <Route path="/risks" element={<RisksPage />} />
        <Route path="/working-items" element={<WorkingItemsPage />} />
        <Route path="/weekly-report" element={<WeeklyReportPage />} />
        <Route path="/admin/users" element={<UsersPage />} />
        <Route path="*" element={<Navigate to="/takeover" replace />} />
      </Route>
    </Routes>
  )
}
